import {
  SVG_CUT_WIDTH_MM,
  SVG_PROJ_FADE,
  SVG_PROJ_WIDTH_MM,
} from './drawingToSvg'
import type { Drawing2D } from './viewTypes'

// ---------------------------------------------------------------------------
// Peso e contraste do traço de PROJECÇÃO.
//
// A hierarquia clássica do desenho técnico é: o corte manda (0,35 mm,
// cor cheia), a projecção é secundária (0,13 mm, cor esbatida). Isso
// pressupõe que HÁ corte. Numa planta de MEP não há: as condutas correm
// abaixo do plano e vê-se a rede de cima, tudo em projecção. Uma vista
// só de projecção é legítima e é o normal em redes, mas com o traço
// secundário sai pálida e ilegível: 0,13 mm esbatidos 40% para o branco.
//
// Aqui resolve-se com uma escolha do UTILIZADOR e um valor por omissão
// que se adapta:
//   - 'auto'  : olha para o desenho. Se a projecção é o assunto (quase
//               não há corte), promove-a a traço primário; senão mantém
//               a hierarquia de sempre.
//   - 'leve'  : o de sempre (0,13 mm, esbatido 0,40).
//   - 'media' : intermédio (0,20 mm, esbatido 0,20).
//   - 'forte' : primário (0,30 mm, cor cheia).
//
// Um só controlo para as duas grandezas de propósito: espessura e
// contraste andam sempre juntas na leitura de um desenho (uma linha
// grossa e pálida lê-se tão mal como uma fina e escura), e dois campos
// numéricos numa barra já cheia davam mais maneiras de estragar do que
// de acertar. O 'auto' nunca se impõe: é só o valor de partida.
//
// A escolha aplica-se ao ecrã, ao SVG, ao PDF e ao DXF pelas MESMAS
// contas (é esta função que os três chamam), e grava-se com a vista.
// ---------------------------------------------------------------------------

export type ProjWeightChoice = 'auto' | 'leve' | 'media' | 'forte'

export const PROJ_WEIGHT_CHOICES: ProjWeightChoice[] = [
  'auto',
  'leve',
  'media',
  'forte',
]

export interface LineWeights {
  /** Espessura do traço de corte, em mm de papel. */
  cutWidthMm: number
  /** Espessura do traço de projecção, em mm de papel. */
  projWidthMm: number
  /** Quanto a cor da projecção se aproxima do branco (0 = cor cheia). */
  projFade: number
}

/** Presets do traço de projecção. */
export const PROJ_WEIGHT_PRESETS: Record<
  Exclude<ProjWeightChoice, 'auto'>,
  { widthMm: number; fade: number }
> = {
  leve: { widthMm: SVG_PROJ_WIDTH_MM, fade: SVG_PROJ_FADE },
  media: { widthMm: 0.2, fade: 0.2 },
  forte: { widthMm: 0.3, fade: 0 },
}

/** Etiquetas para a UI. */
export const PROJ_WEIGHT_LABELS: Record<ProjWeightChoice, string> = {
  auto: 'Automática',
  leve: 'Leve',
  media: 'Média',
  forte: 'Forte',
}

/** Abaixo desta fracção de linhas de corte, o desenho é dominado pela
 *  projecção e o 'auto' promove-a. 2% é folgado: uma planta de
 *  arquitectura corrente anda muito acima disso (as paredes, os pilares
 *  e as lajes cortadas dão logo dezenas de por cento), e uma rede de
 *  MEP sem nada cortado dá exactamente zero. */
export const PROJ_DOMINANT_MAX_CUT_SHARE = 0.02

/** Fracção de polilinhas que são de corte. 0 quando o desenho está
 *  vazio (não há hierarquia nenhuma a defender). */
export function cutShare(d: Drawing2D | null | undefined): number {
  if (!d) return 0
  let cut = 0
  let proj = 0
  for (const l of d.layers) {
    cut += l.cut.length
    proj += l.projection.length
  }
  const total = cut + proj
  return total > 0 ? cut / total : 0
}

/** true quando a vista é (quase) só projecção: é a projecção que tem de
 *  se ler, não o corte. */
export function isProjectionDominant(d: Drawing2D | null | undefined): boolean {
  if (!d) return false
  let cut = 0
  let proj = 0
  for (const l of d.layers) {
    cut += l.cut.length
    proj += l.projection.length
  }
  if (proj === 0) return false
  return cut / (cut + proj) < PROJ_DOMINANT_MAX_CUT_SHARE
}

/** O que o 'auto' escolhe para este desenho. */
export function autoProjWeight(
  d: Drawing2D | null | undefined,
): Exclude<ProjWeightChoice, 'auto'> {
  return isProjectionDominant(d) ? 'forte' : 'leve'
}

/** Resolve a escolha do utilizador em espessuras e contraste concretos.
 *  `choice` ausente ou inválida vale 'auto'. */
export function resolveLineWeights(
  d: Drawing2D | null | undefined,
  choice?: ProjWeightChoice,
): LineWeights & { applied: Exclude<ProjWeightChoice, 'auto'> } {
  const wanted: ProjWeightChoice =
    choice && PROJ_WEIGHT_CHOICES.includes(choice) ? choice : 'auto'
  const applied = wanted === 'auto' ? autoProjWeight(d) : wanted
  const preset = PROJ_WEIGHT_PRESETS[applied]
  return {
    cutWidthMm: SVG_CUT_WIDTH_MM,
    projWidthMm: preset.widthMm,
    projFade: preset.fade,
    applied,
  }
}

/** Valida o que vem do armazenamento. */
export function sanitizeProjWeight(raw: unknown): ProjWeightChoice | undefined {
  return typeof raw === 'string' &&
    PROJ_WEIGHT_CHOICES.includes(raw as ProjWeightChoice)
    ? (raw as ProjWeightChoice)
    : undefined
}
